import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import supabase from "../config/supabase";
import { isBrowser } from "../lib/utils.jsx";

const AuthContext = createContext({});

export function AuthProvider({ children }) {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const location = useLocation();

	const pathname = location.pathname;

	useEffect(() => {
		const checkAuth = async () => {
			if (!isBrowser) {
				setLoading(false);
				return;
			}

			const {
				data: { session },
			} = await supabase.auth.getSession();

			if (!session) {
				setLoading(false);
				setUser(null);
				return;
			}

			setUser(session.user);
			setLoading(false);
		};

		checkAuth();

		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange(async (event, session) => {
			setUser(session?.user ?? null);
			setLoading(false);
		});

		return () => subscription?.unsubscribe();
	}, [pathname]);

	const signUp = async (email, password, userData = {}) => {
		const { data, error } = await supabase.auth.signUp({
			email,
			password,
			options: {
				data: userData,
			},
		});
		return { data, error };
	};

	const signInWithEmail = async (email, password) => {
		const { data, error } = await supabase.auth.signInWithPassword({
			email,
			password,
		});
		return { data, error };
	};

	const signInWithGoogle = async (redirectPath = "/dashboard") => {
		try {
			const { data, error } = await supabase.auth.signInWithOAuth({
				provider: "google",
				options: {
					redirectTo: `${window.location.origin}${redirectPath}`,
				},
			});

			if (error) throw error;
			return { data, error: null };
		} catch (error) {
			console.error("Error signing in with Google:", error);
			return { data: null, error: error.message };
		}
	};

	const signOut = async () => {
		const { error } = await supabase.auth.signOut();
		return { error };
	};

	const resetPassword = async (email) => {
		const { data, error } =
			await supabase.auth.resetPasswordForEmail(email);
		return { data, error };
	};

	const updateProfile = async (updates) => {
		const { data, error } = await supabase.auth.updateUser({
			data: updates,
		});
		return { data, error };
	};

	// Logout function
	const logout = async () => {
		await signOut();
	};

	const value = {
		user,
		loading,
		isAuthenticated: !!user,
		signUp,
		signInWithEmail,
		signInWithGoogle,
		signOut,
		logout,
		resetPassword,
		updateProfile,
	};

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}

// Auth Guard Components
export function AuthRequired({ children }) {
	const { loading } = useAuth();

	if (loading) {
		return <div>Loading...</div>;
	}

	// Remove the redirect - allow access without authentication
	return <>{children}</>;
}

export function AuthRedirect({ children }) {
	const { isAuthenticated, loading } = useAuth();
	const navigate = useNavigate();

	if (loading) {
		return <div>Loading...</div>; // Replace with your loading component
	}

	if (isAuthenticated) {
		navigate("/dashboard", { replace: true });
		return null;
	}

	return <>{children}</>;
}
